import React, { useCallback } from 'react';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BUILDING_FOOTPRINT_TEMPLATES,
  FOOTPRINT_TEMPLATE_DRAG_TYPE,
  type BuildingFootprintTemplateId,
} from '../helpers/building-footprint-template.helpers';
import {
  BUILDING_FOOTPRINT_PREVIEW_HEIGHT,
  BUILDING_FOOTPRINT_PREVIEW_STYLE,
  BUILDING_FOOTPRINT_PREVIEW_WIDTH,
  getBuildingFootprintPreviewPath,
} from '../helpers/building-footprint-template-preview.helpers';
import {
  useBuildingCandidateFootprintTemplate,
  useRequestFootprintCenterPlacement,
  useSetBuildingCandidateFootprintTemplate,
} from '../store/design-store.hooks';
import { PANEL_CONTENT_DEFAULT_SPACING } from '@ui/layout/panels/panel.constants';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
  },
  dividerSpacing: {
    width: `calc(100% + ${theme.spacing(PANEL_CONTENT_DEFAULT_SPACING * 2)})`,
    marginLeft: theme.spacing(-PANEL_CONTENT_DEFAULT_SPACING),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  divider: {
    width: '100%',
    height: 1,
    background: 'rgba(0, 0, 0, 0.1)',
  },
  sectionLabel: {
    marginBottom: theme.spacing(1),
    // Align with the Storeys label after the back-button column and grid gap.
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: `calc(28px + ${theme.spacing(0.5)} + 4px)`,
    paddingRight: 4,
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: '0.7px',
    lineHeight: 1.56,
    textTransform: 'uppercase',
  },
  list: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(PANEL_CONTENT_DEFAULT_SPACING),
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  listItem: {
    minWidth: 0,
  },
  customListItem: {
    gridColumn: '1 / -1',
  },
  option: {
    '--footprint-preview-fill': `rgba(0, 0, 0, ${BUILDING_FOOTPRINT_PREVIEW_STYLE.fillOpacity})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: 128,
    padding: theme.spacing(1),
    border: 0,
    borderRadius: 8,
    background: 'transparent',
    color: 'rgba(0, 0, 0, 0.6)',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      '--footprint-preview-fill': `rgba(0, 0, 0, ${BUILDING_FOOTPRINT_PREVIEW_STYLE.activeFillOpacity})`,
      background: 'rgba(0, 0, 0, 0.05)',
      color: 'rgba(0, 0, 0, 0.8)',
    },
    '&[aria-pressed="true"]': {
      '--footprint-preview-fill': `rgba(0, 0, 0, ${BUILDING_FOOTPRINT_PREVIEW_STYLE.activeFillOpacity})`,
      background: 'rgba(0, 0, 0, 0.05)',
      color: 'rgba(0, 0, 0, 1)',
    },
  },
  customOption: {
    minHeight: 56,
  },
  preview: {
    width: '100%',
    height: BUILDING_FOOTPRINT_PREVIEW_HEIGHT,
    flexShrink: 0,
    overflow: 'visible',
  },
  optionLabel: {
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: '0.06em',
    lineHeight: 1.4,
  },
}));

export const BuildingFootprintTemplateList: React.FC = () => {
  const { classes, cx } = useStyles();
  const requestCenterPlacement = useRequestFootprintCenterPlacement();
  const selectedTemplate = useBuildingCandidateFootprintTemplate();
  const setSelectedTemplate = useSetBuildingCandidateFootprintTemplate();
  const handleSelect = useCallback(
    (template: BuildingFootprintTemplateId) => setSelectedTemplate(template),
    [setSelectedTemplate],
  );

  return (
    <div className={classes.root}>
      <div className={classes.dividerSpacing} aria-hidden="true">
        <div className={classes.divider} />
      </div>
      <Typography className={classes.sectionLabel}>Footprint</Typography>
      <ul className={classes.list}>
        {BUILDING_FOOTPRINT_TEMPLATES.map((template) => {
          const previewPath =
            template.id === 'custom'
              ? undefined
              : getBuildingFootprintPreviewPath(template);
          return (
            <li
              key={template.id}
              className={cx(
                classes.listItem,
                template.id === 'custom' && classes.customListItem,
              )}
            >
              <button
                type="button"
                className={cx(
                  classes.option,
                  template.id === 'custom' && classes.customOption,
                )}
                aria-label={template.label}
                aria-pressed={selectedTemplate === template.id}
                draggable={template.id !== 'custom'}
                onDragStart={(event) => {
                  if (template.id === 'custom') return;
                  handleSelect(template.id);
                  event.dataTransfer.effectAllowed = 'copy';
                  event.dataTransfer.setData(
                    FOOTPRINT_TEMPLATE_DRAG_TYPE,
                    template.id,
                  );
                }}
                onClick={() => handleSelect(template.id)}
                onDoubleClick={() => {
                  if (template.id === 'custom') return;
                  handleSelect(template.id);
                  requestCenterPlacement();
                }}
              >
                {template.id === 'custom' ? (
                  <Typography className={classes.optionLabel}>
                    {template.label}
                  </Typography>
                ) : (
                  <svg
                    className={classes.preview}
                    viewBox={`0 0 ${BUILDING_FOOTPRINT_PREVIEW_WIDTH} ${BUILDING_FOOTPRINT_PREVIEW_HEIGHT}`}
                    aria-hidden="true"
                  >
                    <defs>
                      <clipPath id={`footprint-${template.id}-clip`}>
                        <path d={previewPath} clipRule="evenodd" />
                      </clipPath>
                    </defs>
                    <path
                      d={previewPath}
                      fill="var(--footprint-preview-fill)"
                      fillRule="evenodd"
                    />
                    <path
                      d={previewPath}
                      fill="none"
                      stroke={`rgba(0, 0, 0, ${BUILDING_FOOTPRINT_PREVIEW_STYLE.strokeOpacity})`}
                      strokeWidth={
                        BUILDING_FOOTPRINT_PREVIEW_STYLE.strokeWidth * 2
                      }
                      vectorEffect="non-scaling-stroke"
                      clipPath={`url(#footprint-${template.id}-clip)`}
                    />
                  </svg>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
